import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useProjectStore } from '@/store/projectStore';
import { createImageOverlay } from '@/utils/overlayFactory';
import QRCode from 'qrcode';
import clsx from 'clsx';
import {
    QrCode,
    X,
    Link,
    Instagram,
    Twitter,
    Youtube,
    Globe,
    Smartphone,
    GripHorizontal,
} from 'lucide-react';

interface QRPreset {
    id: string;
    name: string;
    icon: React.ReactNode;
    prefix: string;
    placeholder: string;
}

const qrPresets: QRPreset[] = [
    {
        id: 'url',
        name: 'Website URL',
        icon: <Globe size={18} />,
        prefix: '',
        placeholder: 'https://example.com',
    },
    {
        id: 'instagram',
        name: 'Instagram',
        icon: <Instagram size={18} />,
        prefix: 'https://instagram.com/',
        placeholder: 'username',
    },
    {
        id: 'twitter',
        name: 'X (Twitter)',
        icon: <Twitter size={18} />,
        prefix: 'https://x.com/',
        placeholder: 'username',
    },
    {
        id: 'youtube',
        name: 'YouTube',
        icon: <Youtube size={18} />,
        prefix: 'https://youtube.com/@',
        placeholder: 'channel',
    },
    {
        id: 'phone',
        name: 'Phone Number',
        icon: <Smartphone size={18} />,
        prefix: 'tel:',
        placeholder: '+1234567890',
    },
];

export const QRCodeGenerator = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState<string>('url');
    const [inputValue, setInputValue] = useState('');
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [qrSize] = useState(150);
    const [darkColor, setDarkColor] = useState('#000000');
    const [lightColor, setLightColor] = useState('#ffffff');
    const [isGenerating, setIsGenerating] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ x: 0, y: 80 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const panelRef = useRef<HTMLDivElement>(null);

    const video = useProjectStore((state) => state.project.video);
    const addOverlay = useProjectStore((state) => state.addOverlay);
    const selectOverlay = useProjectStore((state) => state.selectOverlay);

    // Get the current preset
    const currentPreset = qrPresets.find((p) => p.id === selectedPreset) || qrPresets[0];

    // Center panel when opened
    useEffect(() => {
        if (isOpen) {
            setPanelPosition({ x: window.innerWidth / 2 - 200, y: 80 });
        }
    }, [isOpen]);

    // Drag handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX - panelPosition.x,
            y: e.clientY - panelPosition.y,
        };
    }, [panelPosition]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            setPanelPosition({
                x: e.clientX - dragStartRef.current.x,
                y: e.clientY - dragStartRef.current.y,
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // Generate QR code whenever inputs change
    useEffect(() => {
        const generateQR = async () => {
            const fullValue = currentPreset.prefix + inputValue;
            if (!inputValue) {
                setQrDataUrl(null);
                return;
            }

            try {
                setIsGenerating(true);
                const dataUrl = await QRCode.toDataURL(fullValue, {
                    width: qrSize,
                    margin: 1,
                    color: {
                        dark: darkColor,
                        light: lightColor,
                    },
                    errorCorrectionLevel: 'M',
                });
                setQrDataUrl(dataUrl);
            } catch (err) {
                console.error('QR generation error:', err);
                setQrDataUrl(null);
            } finally {
                setIsGenerating(false);
            }
        };

        const debounce = setTimeout(generateQR, 300);
        return () => clearTimeout(debounce);
    }, [inputValue, currentPreset.prefix, qrSize, darkColor, lightColor]);

    const handleAddToCanvas = useCallback(() => {
        if (!qrDataUrl || !video) return;

        // Calculate position (bottom-right by default)
        const overlaySize = Math.min(video.width, video.height) * 0.15; // 15% of smallest dimension
        const margin = 20;

        const overlay = createImageOverlay({
            bounds: {
                x: video.width - overlaySize - margin,
                y: video.height - overlaySize - margin,
                width: overlaySize,
                height: overlaySize,
            },
            startTime: 0,
            endTime: video.duration,
            src: qrDataUrl,
            originalWidth: qrSize,
            originalHeight: qrSize,
        });

        // Rename the overlay
        overlay.name = `QR: ${currentPreset.name}`;

        addOverlay(overlay);
        selectOverlay(overlay.id);
        setIsOpen(false);
        setInputValue('');
    }, [qrDataUrl, video, qrSize, currentPreset.name, addOverlay, selectOverlay]);

    if (!video) return null;

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    'btn btn-secondary text-sm flex items-center gap-2',
                    isOpen && 'ring-2 ring-editor-accent'
                )}
                title="Generate QR Code"
            >
                <QrCode size={16} />
                <span>QR Code</span>
            </button>

            {isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setIsOpen(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 99998,
                        }}
                    />

                    {/* Panel - Draggable with semi-transparent background */}
                    <div 
                        ref={panelRef}
                        style={{
                            position: 'fixed',
                            top: panelPosition.y,
                            left: panelPosition.x,
                            width: '400px',
                            backgroundColor: 'rgba(26, 26, 46, 0.85)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
                            zIndex: 99999,
                            overflow: 'hidden',
                            border: '1px solid rgba(61, 61, 92, 0.8)',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                        }}
                    >
                        {/* Drag Handle Header */}
                        <div 
                            onMouseDown={handleMouseDown}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                backgroundColor: 'rgba(37, 37, 66, 0.9)',
                                borderBottom: '1px solid rgba(61, 61, 92, 0.8)',
                                cursor: isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <GripHorizontal size={16} style={{ color: '#6b7280' }} />
                                <QrCode size={18} style={{ color: '#818cf8' }} />
                                <h3 style={{ fontWeight: 600, color: '#ffffff', margin: 0, fontSize: '16px' }}>QR Code Generator</h3>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                style={{
                                    padding: '4px',
                                    borderRadius: '6px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                <X size={16} style={{ color: '#9ca3af' }} />
                            </button>
                        </div>

                        {/* Preset Selection */}
                        <div style={{ padding: '16px', backgroundColor: 'rgba(26, 26, 46, 0.5)', borderBottom: '1px solid rgba(61, 61, 92, 0.8)' }}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Link Type</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {qrPresets.map((preset) => (
                                    <button
                                        key={preset.id}
                                        onClick={() => {
                                            setSelectedPreset(preset.id);
                                            setInputValue('');
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            backgroundColor: selectedPreset === preset.id ? '#6366f1' : '#374151',
                                            color: selectedPreset === preset.id ? '#ffffff' : '#d1d5db',
                                        }}
                                    >
                                        {preset.icon}
                                        <span>{preset.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Input */}
                        <div style={{ padding: '16px', backgroundColor: 'rgba(26, 26, 46, 0.5)', borderBottom: '1px solid rgba(61, 61, 92, 0.8)' }}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {currentPreset.name}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {currentPreset.prefix && (
                                    <span style={{ fontSize: '14px', color: '#9ca3af', backgroundColor: '#374151', padding: '8px', borderRadius: '8px' }}>
                                        {currentPreset.prefix}
                                    </span>
                                )}
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={currentPreset.placeholder}
                                    style={{
                                        flex: 1,
                                        backgroundColor: '#374151',
                                        border: '1px solid #4b5563',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        color: '#ffffff',
                                        outline: 'none',
                                    }}
                                />
                            </div>
                        </div>

                        {/* QR Preview */}
                        <div style={{ padding: '16px', backgroundColor: 'rgba(26, 26, 46, 0.5)', borderBottom: '1px solid rgba(61, 61, 92, 0.8)' }}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</p>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {qrDataUrl ? (
                                    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: '#ffffff', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
                                        <img
                                            src={qrDataUrl}
                                            alt="QR Code Preview"
                                            style={{ width: '150px', height: '150px' }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        width: '150px',
                                        height: '150px',
                                        borderRadius: '8px',
                                        backgroundColor: '#374151',
                                        border: '2px dashed #4b5563',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <span style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', padding: '16px' }}>
                                            {isGenerating ? 'Generating...' : 'Enter a link to preview'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Customization */}
                        <div style={{ padding: '16px', backgroundColor: 'rgba(26, 26, 46, 0.5)', borderBottom: '1px solid rgba(61, 61, 92, 0.8)' }}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customize</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '4px' }}>QR Color</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={darkColor}
                                            onChange={(e) => setDarkColor(e.target.value)}
                                            style={{ width: '32px', height: '32px', borderRadius: '4px', cursor: 'pointer', border: 'none' }}
                                        />
                                        <span style={{ fontSize: '12px', color: '#ffffff' }}>{darkColor}</span>
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: '#9ca3af', display: 'block', marginBottom: '4px' }}>Background</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={lightColor}
                                            onChange={(e) => setLightColor(e.target.value)}
                                            style={{ width: '32px', height: '32px', borderRadius: '4px', cursor: 'pointer', border: 'none' }}
                                        />
                                        <span style={{ fontSize: '12px', color: '#ffffff' }}>{lightColor}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add Button */}
                        <div style={{ padding: '16px', backgroundColor: 'rgba(26, 26, 46, 0.5)' }}>
                            <button
                                onClick={handleAddToCanvas}
                                disabled={!qrDataUrl}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    fontSize: '14px',
                                    border: 'none',
                                    cursor: qrDataUrl ? 'pointer' : 'not-allowed',
                                    backgroundColor: qrDataUrl ? '#6366f1' : '#4b5563',
                                    color: qrDataUrl ? '#ffffff' : '#9ca3af',
                                }}
                            >
                                <Link size={16} />
                                Add QR Code to Video
                            </button>
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

